import React, { useState, useContext, useEffect, useRef } from 'react'
import Context from '../utils/Context'
import ROSLIB from 'roslib'
import { measureConnectionLatency, imuCalibration } from '../utils/ROS/ROSFunctions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWifi, faBullseye, faCompass, faBolt, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'

const DeveloperTools = () => {
  const context = useContext(Context)
  
  // State for Test Latency
  const [latency, setLatency] = useState(null)
  const [latencyFeedback, setLatencyFeedback] = useState(null)
  const [lastLatencyCheck, setLastLatencyCheck] = useState(null)
  
  // State for Capture TF
  const [tfCapture, setTfCapture] = useState({
    isCapturing: false,
    data: null,
    suggestions: null
  })
  const [tfFeedback, setTfFeedback] = useState(null)
  const tfSubscriber = useRef(null)
  
  // State for IMU Calibration
  const [imuFeedback, setImuFeedback] = useState(null)
  
  // State for Release Charge
  const [releaseFeedback, setReleaseFeedback] = useState(null)

  // Clean up TF subscriber on unmount
  useEffect(() => {
    return () => {
      if (tfSubscriber.current) {
        tfSubscriber.current.unsubscribe()
        tfSubscriber.current = null
      }
    }
  }, [])

  // Function to measure connection latency
  const handleTestLatency = async () => {
    if (!context.selectedRobot?.ip) {
      setLatencyFeedback({ type: 'error', message: 'Nenhum robô selecionado' })
      return
    }

    setLatencyFeedback({ type: 'info', message: 'Testando latência...' })
    setLatency(null)

    try {
      const latencyResult = await measureConnectionLatency(context.selectedRobot.ip)
      setLatency(latencyResult)
      setLastLatencyCheck(new Date())
      
      if (latencyResult) {
        setLatencyFeedback({ 
          type: 'success', 
          message: `Latência: ${latencyResult.toFixed(2)}ms` 
        })
      } else {
        setLatencyFeedback({ 
          type: 'warning', 
          message: 'Não foi possível medir a latência' 
        })
      }
    } catch (error) {
      setLatencyFeedback({ 
        type: 'error', 
        message: 'Erro ao testar latência: ' + error.message 
      })
    }

    setTimeout(() => setLatencyFeedback(null), 5000)
  }

  // Function to capture TF data
  const handleCaptureTF = () => {
    if (!context.selectedRobot || !window.ros) {
      setTfFeedback({ type: 'error', message: 'ROS não conectado' })
      setTimeout(() => setTfFeedback(null), 3000)
      return
    }

    setTfCapture(prev => ({ ...prev, isCapturing: true, data: null, suggestions: null }))
    setTfFeedback({ type: 'info', message: 'Capturando dados TF...' })
    
    const tfTopic = new ROSLIB.Topic({
      ros: window.ros,
      name: '/tf_static',
      messageType: 'tf2_msgs/msg/TFMessage',
      qos: {
        durability: 'transient_local',
        reliability: 'reliable'
      }
    })

    tfSubscriber.current = tfTopic

    tfTopic.subscribe((message) => {
      console.log('TF Data received:', message)
      setTfCapture(prev => ({ ...prev, data: message }))
      
      // Process TF data to extract laser transforms
      const laserTransforms = extractLaserTransforms(message)
      setTfCapture(prev => ({ 
        ...prev, 
        suggestions: laserTransforms,
        isCapturing: false 
      }))
      
      setTfFeedback({ 
        type: 'success', 
        message: `TF capturado! ${Object.keys(laserTransforms).length} lasers encontrados` 
      })
      
      setTimeout(() => setTfFeedback(null), 5000)
      
      // Stop capturing after first message (tf_static doesn't change)
      tfTopic.unsubscribe()
      tfSubscriber.current = null
    })
  }

  const extractLaserTransforms = (tfMessage) => {
    const transforms = tfMessage.transforms || []
    const suggestions = {}
    
    // Look for laser-related transforms
    transforms.forEach(tf => {
      const childFrame = tf.child_frame_id
      const parentFrame = tf.header.frame_id
      const translation = tf.transform.translation
      const rotation = tf.transform.rotation
      
      // Check if this is a laser frame
      if (childFrame.includes('laser') || childFrame.includes('scan')) {
        console.log(`Found laser transform: ${parentFrame} -> ${childFrame}`)
        
        // Calculate yaw from quaternion (assuming mostly 2D rotation)
        const yaw = Math.atan2(
          2.0 * (rotation.w * rotation.z + rotation.x * rotation.y),
          1.0 - 2.0 * (rotation.y * rotation.y + rotation.z * rotation.z)
        )
        
        // Determine laser type based on frame name
        let laserType = 'unknown'
        if (childFrame.includes('front')) laserType = 'front'
        else if (childFrame.includes('rear') || childFrame.includes('back')) laserType = 'rear'
        else if (childFrame.includes('left')) laserType = 'left'
        else if (childFrame.includes('right')) laserType = 'right'
        else if (childFrame.includes('top')) laserType = 'top'
        
        suggestions[laserType] = {
          frame: childFrame,
          x: translation.x,
          y: translation.y,
          yaw: yaw,
          original: tf
        }
      }
    })
    
    return suggestions
  }

  const handleApplyTF = () => {
    if (!tfCapture.suggestions) return
    
    console.log('Suggested laser transforms:', tfCapture.suggestions)
    setTfFeedback({ 
      type: 'success', 
      message: `Sugestões TF disponíveis no console (${Object.keys(tfCapture.suggestions).length} lasers)` 
    })
    
    setTimeout(() => setTfFeedback(null), 5000)
  }

  // Function to handle IMU Calibration
  const handleImuCalibration = () => {
    setImuFeedback({ type: 'info', message: 'Enviando comando de calibração IMU...' })
    imuCalibration(context)
    
    setTimeout(() => {
      setImuFeedback({ type: 'success', message: 'Comando de calibração IMU enviado com sucesso!' })
    }, 500)
    
    setTimeout(() => {
      setImuFeedback(null)
    }, 5000)
  }

  // Function to handle Release Charge
  const handleReleaseCharge = () => {
    setReleaseFeedback({ type: 'info', message: 'Enviando comando para liberar carregador...' })
    
    const releaseChargeTask = {
      "robot": context.selectedRobot?.typeRobot || "1500PT",
      "mapIndex": 0,
      "name": "ReleaseCharger",
      "actions": [
        {
          "id": "release-charger",
          "name": "release-charger",
          "needsValue": false,
          "value": ""
        }
      ]
    }

    // Send task to robot
    if (context.sendTask) {
      context.sendTask(releaseChargeTask)
      setReleaseFeedback({ 
        type: 'success', 
        message: 'Comando Release Charger enviado!' 
      })
    } else {
      console.log('Release Charge task:', releaseChargeTask)
      setReleaseFeedback({ 
        type: 'warning', 
        message: 'Função sendTask não disponível. Tarefa logada no console.' 
      })
    }
    
    setTimeout(() => {
      setReleaseFeedback(null)
    }, 5000)
  }

  const FeedbackMessage = ({ feedback, icon }) => {
    if (!feedback) return null
    
    const typeClasses = {
      success: 'is-success',
      error: 'is-danger',
      warning: 'is-warning',
      info: 'is-info'
    }
    
    return (
      <div className={`notification ${typeClasses[feedback.type]} is-light mt-2`}>
        <p>
          <FontAwesomeIcon icon={icon} className="mr-2" />
          {feedback.message}
        </p>
      </div>
    )
  }

  return (
    <div className="container">
      <h1 className="title is-4 mb-4">🛠️ Developer Tools</h1>
      
      <div className="columns is-multiline">
        {/* Test Latency Section */}
        <div className="column is-half">
          <div className="box">
            <h2 className="subtitle is-5 mb-3">
              <FontAwesomeIcon icon={faWifi} className="mr-2" />
              Test Latency
            </h2>
            <p className="mb-3">Medir latência da conexão com o robô</p>
            
            <button
              className="button is-info"
              onClick={handleTestLatency}
              disabled={!context.selectedRobot?.ip}
            >
              <FontAwesomeIcon icon={faWifi} className="mr-2" />
              Testar Latência
            </button>
            
            {lastLatencyCheck && latency && !latencyFeedback && (
              <div className="mt-3">
                <span className="tag is-info is-light">
                  Última verificação: {lastLatencyCheck.toLocaleTimeString()}
                </span>
                <span className="tag is-success ml-2">
                  {latency.toFixed(2)}ms
                </span>
              </div>
            )}
            
            <FeedbackMessage feedback={latencyFeedback} icon={faCheckCircle} />
          </div>
        </div>

        {/* Capture TF Section */}
        <div className="column is-half">
          <div className="box">
            <h2 className="subtitle is-5 mb-3">
              <FontAwesomeIcon icon={faBullseye} className="mr-2" />
              Capture TF
            </h2>
            <p className="mb-3">Capturar transformações TF do robô</p>
            
            <div className="buttons">
              <button
                className={`button ${tfCapture.isCapturing ? 'is-warning' : 'is-primary'}`}
                onClick={handleCaptureTF}
                disabled={!context.selectedRobot?.ip || tfCapture.isCapturing}
              >
                <FontAwesomeIcon icon={faBullseye} className="mr-2" />
                {tfCapture.isCapturing ? 'Capturando...' : 'Capturar TF'}
              </button>
              
              {tfCapture.suggestions && (
                <button
                  className="button is-success"
                  onClick={handleApplyTF}
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                  Ver Sugestões ({Object.keys(tfCapture.suggestions).length})
                </button>
              )}
            </div>
            
            <FeedbackMessage feedback={tfFeedback} icon={faCheckCircle} />
          </div>
        </div>

        {/* IMU Calibration Section */}
        <div className="column is-half">
          <div className="box">
            <h2 className="subtitle is-5 mb-3">
              <FontAwesomeIcon icon={faCompass} className="mr-2" />
              IMU Calibration
            </h2>
            <p className="mb-3">Calibrar sensor IMU do robô</p>
            
            <button
              className="button is-info"
              onClick={handleImuCalibration}
              disabled={!context.selectedRobot?.ip}
            >
              <FontAwesomeIcon icon={faCompass} className="mr-2" />
              Calibrar IMU
            </button>
            
            <FeedbackMessage feedback={imuFeedback} icon={faCheckCircle} />
          </div>
        </div>

        {/* Release Charge Section */}
        <div className="column is-half">
          <div className="box">
            <h2 className="subtitle is-5 mb-3">
              <FontAwesomeIcon icon={faBolt} className="mr-2" />
              Release Charge
            </h2>
            <p className="mb-3">Liberar o carregador do robô</p>
            
            <button
              className="button is-warning"
              onClick={handleReleaseCharge}
              disabled={!context.selectedRobot?.ip}
            >
              <FontAwesomeIcon icon={faBolt} className="mr-2" />
              Release Charger
            </button>
            
            <FeedbackMessage feedback={releaseFeedback} icon={faCheckCircle} />
          </div>
        </div>
      </div>

      {/* Connection Status */}
      <div className="box mt-4">
        <h2 className="subtitle is-6">Status da Conexão</h2>
        <div className="tags">
          <span className={`tag ${context.selectedRobot?.ip ? 'is-success' : 'is-danger'}`}>
            {context.selectedRobot?.ip ? `🟢 Conectado: ${context.selectedRobot.ip}` : '🔴 Não conectado'}
          </span>
          {context.selectedRobot?.typeRobot && (
            <span className="tag is-info">
              Tipo: {context.selectedRobot.typeRobot}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export default DeveloperTools
